import React, { useState, useMemo, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  Typography
} from '@one-thd/sui-atomic-components';
import { extend, QueryProvider } from '@thd-nucleus/data-sources';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { QuickEstimateGroup } from './QuickEstimateGroup';
import { QuickIntentResolve } from '../quick-intent-resolve/QuickIntentResolve';
import { QuickEstimateFooter } from './QuickEstimateFooter';

export const QuickEstimateDrawer = ({
  open,
  onClose,
  materials
}) => {
  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';
  const [resolvedIntents, setResolvedIntents] = useState(new Map());
  const [groupTotals, setGroupTotals] = useState({});
  const [progress, setProgress] = useState({ isResolving: false });

  const itemCount = useMemo(() => {
    const allItems = (materials || []).flatMap((group) => group.items);
    return allItems.length;
  }, [materials]);
  // Collect all intents that need resolution
  // Use a counter to ensure unique intentIds even for duplicate intents in the same group
  const intentsToResolve = useMemo(() => {

    const intents = (materials || []).flatMap((group) => group.items)
      .filter((item) => item?.intent?.trim().length > 0 && !item?.itemId)
      .map((item) => item?.intent);
    return Array.from(new Set(intents));
  }, [materials]);

  const resolveItem = useCallback((item, intentsMap) => {
    const resolvedItem = { ...item, isAutoResolved: false, isBundle: null, resolveFailed: false };
    if (resolvedItem.itemId) {
      return [resolvedItem];
    }
    const resolvedItemIds = intentsMap[item.intent];
    if (resolvedItemIds?.length) {
      const isBundle = resolvedItemIds.length > 1;
      return resolvedItemIds.map((itemId) => ({
        ...resolvedItem,
        isAutoResolved: true,
        itemId,
        isBundle
      }));
    } else if (resolvedItemIds === null) {
      // Intent resolution failed, mark item as unresolved
      return [
        {
          ...resolvedItem,
          resolveFailed: true
        }
      ];
    }
    return [resolvedItem];
  }, []);

  const resolvedMaterials = useMemo(() => {
    return materials?.map((group) => ({
      ...group,
      items: group.items?.flatMap((item) => resolveItem(item, resolvedIntents)) || []
    }));
  }, [materials, resolvedIntents, resolveItem]);

  const updateGroupTotal = useCallback((groupName, total) => {
    setGroupTotals((prevTotals) => ({
      ...prevTotals,
      [groupName]: total
    }));
  }, []);

  // Callback when an intent is resolved (or failed to resolve)
  const handleIntentResolved = useCallback((intent, itemIds) => {
    setResolvedIntents((prev) => {
      return {
        ...prev,
        [intent]: itemIds
      };
    });
  }, []);

  // Reset resolved items when drawer closes
  const handleClose = useCallback(() => {
    setResolvedIntents(new Map());
    onClose();
  }, [onClose]);

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      size={isMobile ? 'full' : 'md'}
    >
      <DrawerHeader
        title="Quick Estimate"
        onClose={handleClose}
      />
      <DrawerBody>
        <QueryProvider
          cacheKey="quick-estimate-drawer"
        >
          {/* Hidden resolver components for parallel intent resolution */}
          <QuickIntentResolve
            intents={intentsToResolve}
            itemCount={itemCount}
            onEachResolved={handleIntentResolved}
            updateProgress={setProgress}
            showLoader
          />
          <div className="sui-flex sui-flex-col sui-gap-4">
            {
              !progress.isResolving && (
                <Typography variant="body-base" weight="bold">
                  The estimated subtotal below is based on the following product recommendations and selected quantities.
                </Typography>
              )
            }
            <div className="sui-flex sui-flex-col">
              {(resolvedMaterials || []).map((group, index) => (
                <QuickEstimateGroup
                  key={group.groupName || `quick-estimate-group-${index}`}
                  isResolvingIntents={progress.isResolving}
                  group={group}
                  index={index}
                  updateGroupTotal={updateGroupTotal}
                />
              ))}
            </div>

            {(!resolvedMaterials || resolvedMaterials.length === 0) && (
              <div className="sui-p-8 sui-text-center">
                <Typography variant="body-base" color="subtle">
                  No materials to estimate. Add items to your list first.
                </Typography>
              </div>
            )}
          </div>
        </QueryProvider>
      </DrawerBody>

      <DrawerFooter position="center" style={{ boxShadow: '0 1px 8px 0 rgb(0 0 0 / 0.375)' }}>
        <QuickEstimateFooter
          handleClose={handleClose}
          loading={progress.isResolving}
          groupTotals={groupTotals}
          numberOfGroups={materials.length}
        />
      </DrawerFooter>
    </Drawer>
  );
};

QuickEstimateDrawer.displayName = 'QuickEstimateDrawer';

QuickEstimateDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  materials: PropTypes.arrayOf(PropTypes.shape({
    groupName: PropTypes.string,
    items: PropTypes.arrayOf(PropTypes.shape({
      itemId: PropTypes.string,
      quantity: PropTypes.number,
      intent: PropTypes.string
    }))
  }))
};

QuickEstimateDrawer.defaultProps = {
  materials: []
};

QuickEstimateDrawer.dataModel = extend(QuickEstimateGroup);
